<template>
  <div :class="{
    'pnlm-container-huygens':
      getAppName() == 'huygens',
    'pnlm-container-dordrecht5D':
      getAppName() == 'dordrecht5D',
    'pnlm-container-canals5D':
      getAppName() == 'canals5D'
  }">
    <div v-show="isViewerLoaded" class="back-button" :class="{
      'back-button-huygens':
        getAppName() == 'huygens',
      'back-button-dordrecht5D':
        getAppName() == 'dordrecht5D',
      'back-button-canals5D':
        getAppName() == 'canals5D'
    }" @click="() => $router.push({ name: 'map-view' })">
      <BaseIcon icon="arrow-left" />
    </div>

    <div v-show="isViewerLoaded" class="info-button" :class="{
      'info-button-huygens':
        getAppName() == 'huygens',
      'info-button-dordrecht5D':
        getAppName() == 'dordrecht5D',
      'info-button-canals5D':
        getAppName() == 'canals5D'
    }" @click="() => infoDrawer = !infoDrawer">
      <BaseIcon icon="info" size="35px" />
    </div>

    <ArticleDrawer />
    <InfoDrawer v-show="isViewerLoaded" :hotspot="hotspot" :show="infoDrawer"
      @onInfoDrawerClose="() => infoDrawer = !infoDrawer" />
    <TimeLayerDrawer v-show="isViewerLoaded" @on5DMode="select5DMode" @onExpertMode="selectExpertMode"
      @onTimelayerChange="selectTimeLayer" />

    <div v-if="isSceneLoading" id="loader">
      <div>
        <span>{{ $t("panorama.loading") }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue"
import { mapState } from "vuex";
import TimeLayerDrawer from "@/components/TimeLayerDrawer.vue";
import InfoDrawer from "@/components/InfoDrawer.vue";
import ArticleDrawer from "@/components/ArticleDrawer.vue";
import { appIdentifier } from "@/utils/appIdentifier.js";


export default {
  name: "PanoramaViewer",
  components: {
    ArticleDrawer,
    InfoDrawer,
    TimeLayerDrawer
  },
  data() {
    return {
      viewer: null,
      start_hfov: 100,
      min_hfov: 45,
      max_hfov: 100,
      selectedTimeLayer: this.hotspotLayer,
      currentHotspots: [],
      currentHotspotPoi: null,
      infoDrawer: true,
      isSceneLoading: false,
      isViewerLoaded: false
    };
  },
  watch: {
    currentHotspotPoi: function (val) {
      let hotspotArticle = this.hotspotPois.find(hotspotPoi => hotspotPoi.id == val.id);

      //Zoom to POI on click
      if (hotspotArticle) {
        const { yaw, pitch } = this.convertXYtoYawPitch(hotspotArticle.x, hotspotArticle.y)
        this.viewer.setYaw(yaw + 33);
        this.viewer.setPitch(pitch);
        this.viewer.setHfov(this.start_hfov);
      }
    },
  },
  computed: {
    ...mapState({
      hotspot: state => state.hotspot,
      selectedArticle: state => state.selectedArticle,
      hotspotPois: state => state.hotspotPois,
      hotspotLayer: state => state.hotspotLayer
    })
  },
  async mounted() {
    this.$store.commit(this.$mutations.CLEAR_ARTICLE_STACK);
    const hotspot = await this.$store.dispatch(this.$actions.GET_HOTSPOT, this.$route.params.id);
    const externalUrl = hotspot.data?.data?.external_url;

    if (/^http/i.test(externalUrl ?? "")) {
      // If a hotspot has an external URL, it should be considered as an external hotspot.
      // We don't have to handle these hotspots ourselves, so we should redirect ASAP.
      // To prevent accidental redirects, we make sure the URL starts with http(s).
      window.location.replace(externalUrl);
    } else {
      this.createPanoramaViewer();
    }
  },
  beforeDestroy() {
    this.viewer.destroy();
  },
  methods: {
    getAppName() {
      return appIdentifier();
    },
    select5DMode(selected) {
      if (selected) {
        document.querySelectorAll('.pnlm-hotspot-base').forEach(h => h.classList.remove('hide-hotspot'));
      } else {
        document.querySelectorAll('.pnlm-hotspot-base').forEach(h => h.classList.add('hide-hotspot'));
      }
    },
    selectExpertMode(year) {
      if (this.viewer.getScene() == `${year}_expert`) {
        this.viewer.loadScene(year, this.viewer.getPitch(), this.viewer.getYaw(), this.viewer.getHfov());
      } else {
        this.viewer.loadScene(`${year}_expert`, this.viewer.getPitch(), this.viewer.getYaw(), this.viewer.getHfov());
      }
    },
    selectTimeLayer(layer) {
      let year = `${layer.year}${layer.expert ? '_expert' : ''}`;
      if (this.viewer.getScene() == year) {
        return;
      } else {
        this.$store.commit(this.$mutations.SET_HOTSPOT_POIS, layer?.hotspot_layer_pois);
        this.$store.commit(this.$mutations.CLEAR_ARTICLE_STACK);

        // Add a timout to give the view a change to update
        setTimeout(() => {
          this.isSceneLoading = true;
          this.viewer.loadScene(year, this.viewer.getPitch(), this.viewer.getYaw(), this.viewer.getHfov());
        }, 50);
      }
    },
    createPanoramaViewer() {
      let options = {
        type: "equirectangular",
        default: {
          firstScene: "default",
          sceneFadeDuration: 250
        },
        scenes: {
          default: {
            type: "equirectangular",
            panorama: this.hotspotLayer?.panorama_url,
            autoLoad: true
          }
        },
        yaw: this.hotspotLayer?.panorama_orientation,
        pitch: 0,
        hfov: this.start_hfov,
        minHfov: this.min_hfov,
        maxHfov: this.max_hfov,
        disableKeyboardCtrl: true,
        isAutoRotationOn: false,
        isOrientationOn: false,
        showControls: false,
        compass: false,
        hotSpotDebug: false
      };

      //Create viewer
      this.viewer = window.pannellum.viewer(this.$el, options);

      this.viewer.on("error", (error) => this.$store.commit(this.$mutations.SET_PANORAMA_ERROR, error));
      this.viewer.on("errorcleared", () => this.$store.commit(this.$mutations.SET_PANORAMA_ERROR, null));
      this.viewer.on("load", () => {
        this.createPanoramaHotspots();
        this.isViewerLoaded = true;
        this.isSceneLoading = false;
      });

      //Add scene layers
      this.hotspot.hotspot_layers.forEach((layer) => {
        let sceneId = `${layer.year}${layer.expert ? '_expert' : ''}`;
        this.viewer.addScene(sceneId, {
          type: "equirectangular",
          panorama: layer.panorama_url,
          autoLoad: true,
          sceneFadeDuration: 300,
        })
      })
    },
    createPanoramaHotspots() {
      //Place Hotspot markers on panorama image
      this.hotspotPois.forEach(hotspotPoi => {
        const { yaw, pitch } = this.convertXYtoYawPitch(hotspotPoi.x, hotspotPoi.y)
        let hotspot = {
          yaw: yaw,
          pitch: pitch,
          cssClass: `hotspot hotspot-${appIdentifier()} hotspot-${hotspotPoi.id}`,
          id: hotspotPoi.id,
          createTooltipFunc: this.createHotspotHandler,
          createTooltipArgs: hotspotPoi
        }
        this.viewer.addHotSpot(hotspot);
      });

    },
    createHotspotHandler(hotSpotIcon, hotspotPoi) {
      let elements = document.getElementsByClassName(`hotspot-${hotspotPoi.id}`);
      for (let i = 0; i < elements.length; i++) {
        let element = elements[i];
        if (!element.hasChildNodes()) {
          let iconImg = document.createElement("img");
          iconImg.setAttribute("src", `data:image/jpeg;base64, ${hotspotPoi.icon}`);
          element.appendChild(iconImg);
        }
      }

      hotSpotIcon.addEventListener("click", () => {
        this.$store.dispatch(this.$actions.GET_ARTICLE, hotspotPoi.article_id);
        this.currentHotspotPoi = hotspotPoi;
      });
    },
    convertXYtoYawPitch(x, y) {
      let yaw = (x - 0.5) * 360;
      let pitch = ((y - 0.5) * 180);
      return { yaw, pitch };
    }
  }
};
</script>

<style lang="scss">
.pnlm-container {
  height: 100vh;
  width: 100vw;
  background: none;
  background-color: var(--v-primary-base);
  background-size: 40px 40px;
  background-position: center;
  background-repeat: no-repeat;

  &-huygens {
    background-image: url(~@/assets/images/huygens/loading-indicator.gif);
  }

  &-dordrecht5D {
    background-image: url(~@/assets/images/dordrecht5D/loading-indicator.gif);
  }

  &-canals5D {
    background-image: url(~@/assets/images/canals5D/loading-indicator.svg);
  }
}

.pnlm-load-box {
  z-index: -999;
  opacity: 0;
}

.back-button {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 30px;
  left: 20px;
  height: 50px;
  width: 50px;
  border-radius: 50px;
  z-index: 10;
  cursor: pointer;

  &-huygens {
    background-color: rgba($primary-huygens, 0.8);
  }

  &-dordrecht5D {
    background-color: rgba($brand-dordrecht5D, 0.8);
  }

  &-canals5D {
    background-color: rgba($brand-canals5D, 0.8);
  }
}

.info-button {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 98px;
  left: 20px;
  height: 50px;
  width: 50px;
  border-radius: 50px;
  z-index: 10;
  cursor: pointer;

  &-huygens {
    background-color: rgba($primary-huygens, 0.8);
  }

  &-dordrecht5D {
    background-color: rgba($brand-dordrecht5D, 0.8);
  }

  &-canals5D {
    background-color: rgba($brand-canals5D, 0.8);
  }
}

.hotspot {
  height: 40px;
  width: 40px;
  cursor: pointer;
  border-radius: 20px;

  &-huygens {
    background-color: rgba($secondary-huygens, 0.8);
  }

  &-dordrecht5D {
    background-color: rgba($secondary-dordrecht5D, 0.8);
  }

  &-canals5D {
    background-color: rgba($secondary-canals5D, 0.8);
  }

  img {
    padding: 6px;
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
}

.hide-hotspot {
  visibility: hidden !important;
}

div.hotspot-tooltip span {
  visibility: hidden;
  position: absolute;
  border-radius: 3px;
  background-color: #fff;
  color: #000;
  text-align: center;
  max-width: 600px;
  height: 300px;
  padding: 10px;
  width: 150px;
  margin: 0 auto;
}

#loader {
  position: absolute;
  z-index: 100;
  left: 50%;
  top: calc(50% - 40px);

  div {
    position: relative;
    left: -50%;
    background-color: var(--v-primary-base) !important;
    border-radius: 10px;
    height: 80px;
    display: table-cell;
    vertical-align: middle;

    span {
      display: block;
      padding: 30px 50px;
      color: #ffffff;
      font-size: 1.7rem;
      line-height: 27px;
      letter-spacing: 1.26px;
    }
  }
}
</style>

<style lang="scss">